import React, { useEffect, useState, useCallback } from 'react';
import { Drawer, Table, Space, Tag, message, Button } from 'antd';
import { CloseOutlined, CopyOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useLazyQuery } from '@apollo/client';
import { GET_ORDERS_BY_SORTING_CODE } from '../../graphql/queries/getOrdersBySortingCode';
import { useTranslation } from 'react-i18next';
import ParcelDetailDrawer from '../../../../delivery/parcels/components/ParcelDetailDrawer';
import { getUserInfo } from '../../../../../utils/auth';

// 定义本页面的统计的状态类型 总数 ｜ 未打包数量 ｜ 已打包数量 ｜ 异常数量 ｜ 需退回数量
export type OrderStatus = 'total' | 'unPackNum' | 'packNum' | 'abnormalNum' | 'returnNum';

interface OrderListItem {
  id: string;
  trackingNumber: string;
  overallStatus?: string;
}

interface OrderListDrawerProps {
  open: boolean;
  onClose: () => void;
  sortingCode: string;
  status: OrderStatus;
  title?: string;
  level: string;
  // 新增：接收当前的filter条件
  currentFilters?: {
    currentWarehouseId: number;
    channelId?: number | null;
    arrivedCurrentWarehouseDateRange?: {
      start: string;
      end: string;
    } | null;
    carrierCode?: number | null;
  };
  // 查询类型：PREVIOUS_STATION-按上一站查询，DESTINATION-按目的地查询（选填）
  queryType?: 'PREVIOUS_STATION' | 'DESTINATION';
}

const OrderListDrawer: React.FC<OrderListDrawerProps> = ({
  open,
  onClose,
  sortingCode,
  status,
  title,
  level,
  currentFilters,
  queryType
}) => {
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const { t } = useTranslation();
  const [parcelDetailVisible, setParcelDetailVisible] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string>('');

  const [getOrdersBySortingCode, { loading }] = useLazyQuery(GET_ORDERS_BY_SORTING_CODE, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data?.getOrdersBySortingCode) {
        const { data: orderData, pagination: paginationData } = data.getOrdersBySortingCode;
        setOrders(orderData || []);
        setPagination({
          current: paginationData.page,
          pageSize: paginationData.size,
          total: paginationData.total
        });
      }
    },
    onError: (error) => {
      console.error('Failed to fetch orders:', error);
    }
  });

  const fetchOrders = useCallback((page: number = 1, size: number = 20) => {
    // 使用传入的filter条件，如果没有则使用默认值
    // 注意：这里刻意不向后端传递 arrivedCurrentWarehouseDateRange，以避免时间格式兼容问题
    const filters: any = {
      sortingCode,
      status,
      level,
      currentWarehouseId: currentFilters?.currentWarehouseId || 1,
      channelId: currentFilters?.channelId || null,
      arrivedCurrentWarehouseDateRange: null,
      carrierCode: currentFilters?.carrierCode || null,
      ...(queryType && { queryType })  // 如果提供了queryType则添加，否则不添加（使用默认逻辑）
    };

    getOrdersBySortingCode({
      variables: {
        filters,
        pageRequest: {
          page,
          size
        }
      }
    });
  }, [sortingCode, status, level, currentFilters?.currentWarehouseId, currentFilters?.channelId, currentFilters?.carrierCode, queryType, getOrdersBySortingCode]);

  useEffect(() => {
    if (open && sortingCode) {
      fetchOrders(1, 20);
    }
  }, [open, sortingCode, status, level, fetchOrders]);

  const handleTableChange = (paginationInfo: any) => {
    fetchOrders(paginationInfo.current, paginationInfo.pageSize);
  };

  // 复制所有当前分页的 tracking numbers 到剪贴板
  const copyAllTrackingNumbers = async () => {
    if (orders.length === 0) {
      message.warning('No tracking numbers to copy');
      return;
    }

    const allTrackingNumbers = orders.map(order => order.trackingNumber).join('\n');

    try {
      await navigator.clipboard.writeText(allTrackingNumbers);
      message.success(`${orders.length} tracking numbers copied to clipboard!`);
    } catch (err) {
      // 降级方案：使用传统的document.execCommand
      const textArea = document.createElement('textarea');
      textArea.value = allTrackingNumbers;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        message.success(`${orders.length} tracking numbers copied to clipboard!`);
      } catch (fallbackErr) {
        message.error('Failed to copy tracking numbers');
      }
      document.body.removeChild(textArea);
    }
  };

  // 显示包裹详情
  const showParcelDetail = (orderId: string) => {
    setSelectedOrderId(orderId);
    setParcelDetailVisible(true);
  };

  const getStatusDisplayName = (status: OrderStatus): string => {
    const statusMap = {
      total: 'Total',
      unPackNum: 'Unbagged',
      packNum: 'Bagged', 
      abnormalNum: 'Exception',
      returnNum: 'to Return'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status: OrderStatus): string => {
    const colorMap = {
      total: 'blue',
      unPackNum: 'orange',
      packNum: 'green',
      abnormalNum: 'red',
      returnNum: 'purple'
    };
    return colorMap[status] || 'blue';
  };

  const columns: ColumnsType<OrderListItem> = [
    {
      title: t('wms.progress.columns.no'),
      key: 'index',
      width: 60,
      align: 'left',
      render: (_: any, __: OrderListItem, index: number) => {
        return (pagination.current - 1) * pagination.pageSize + index + 1;
      }
    },
    {
      title: (
        <div className="flex items-center space-x-2">
          <span>{t('wms.progress.columns.trackingNumber')}</span>
          <CopyOutlined
            className="cursor-pointer text-gray-400 hover:text-blue-600 transition-colors"
            style={{ fontSize: '14px' }}
            onClick={(e) => {
              e.stopPropagation();
              copyAllTrackingNumbers();
            }}
            title="Click to copy all tracking numbers on current page"
          />
        </div>
      ),
      dataIndex: 'trackingNumber',
      key: 'trackingNumber',
      align: 'left',
      render: (trackingNumber: string, record: OrderListItem) => (
        <Button
          type="link"
          onClick={() => showParcelDetail(record.id)}
          style={{ padding: 0, height: 'auto' }}
        >
          {trackingNumber}
        </Button>
      )
    },
    {
      title: t('delivery.parcels.label.status', { defaultValue: 'Parcel Status' }),
      dataIndex: 'overallStatus',
      key: 'overallStatus',
      align: 'left',
      render: (overallStatus?: string) => {
        if (!overallStatus) return '-';
        return t(`enum.overallStatus.${overallStatus}`, { defaultValue: overallStatus });
      }
    }
  ];

  return (
    <>
      <Drawer
        title={
          <Space>
            <span>{t('wms.progress.columns.orderList')}</span>
            <Tag color={getStatusColor(status)}>
              {getStatusDisplayName(status)}
            </Tag>
            <span className="text-sm text-gray-500">
              ({sortingCode})
            </span>
          </Space>
        }
        width={800}
        open={open}
        onClose={onClose}
        closeIcon={<CloseOutlined />}
        destroyOnClose
      >
        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-600">{t('wms.progress.columns.sortingCode')}:</span>
                <span className="ml-2 font-mono">{sortingCode}</span>
              </div>
              <div>
                <span className="font-medium text-gray-600">{t('wms.progress.columns.status')}:</span>
                <Tag color={getStatusColor(status)} className="ml-2">
                  {getStatusDisplayName(status)}
                </Tag>
              </div>
              <div>
                <span className="font-medium text-gray-600">{t('wms.progress.columns.level')}:</span>
                <span className="ml-2">{level}</span>
              </div>
              <div>
                <span className="font-medium text-gray-600">{t('wms.progress.columns.totalOrders')}:</span>
                <span className="ml-2 font-semibold">{pagination.total}</span>
              </div>
            </div>
          </div>

          <Table
            columns={columns}
            dataSource={orders}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => t('table.pagination.totalItems', { total }),
              pageSizeOptions: ['10', '20', '50', '100']
            }}
            onChange={handleTableChange}
            size="small"
            className="border rounded-lg"
          />
        </div>
      </Drawer>

      {/* 包裹详情抽屉 */}
      {parcelDetailVisible && (
        <ParcelDetailDrawer
          visible={parcelDetailVisible}
          onClose={() => setParcelDetailVisible(false)}
          orderId={selectedOrderId}
          userRole={getUserInfo()?.account?.role?.name || ''}
        />
      )}
    </>
  );
};

export default OrderListDrawer;
